import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import download from 'downloadjs';
import { useSnackbar } from 'notistack';

import { getApiKey, downloadStrategyChart, freezeStrategy, rebalanceStrategy, sendStrategyToCs } from '../../../../api';
import { ApiDataResponse } from '../../../../api/types/response';

export const useStrategyManage = (strategyId: string, initialFrozen: boolean) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isFrozen, setIsFrozen] = useState<boolean>(initialFrozen);
  const [apiKeys, setApiKeys] = useState<string | null>(null);

  const onError = (e: unknown) => {
    enqueueSnackbar(String(e), { variant: 'error' });
  };
  const onSuccessGeneric = (result: Pick<ApiDataResponse<unknown>, 'success' | 'errorMessage'>) => {
    if (!result.success) {
      enqueueSnackbar(result.errorMessage || 'Ошибка', { variant: 'error' });
      return;
    }
    enqueueSnackbar('Успешно');
  };

  const onRecalcHistory = () => {};

  const resetApiKeys = () => {
    setApiKeys(null);
  };

  const onDownload = () => {
    downloadStrategyChart({ id: strategyId }).then((result) => {
      if (!result.success) return;
      download(
        `data:application/octet-stream;base64,${result.result.contentBase64}`,
        `${result.result.fileName}`,
        'application/octet-stream',
      );
    });
  };

  const freezeMutation = useMutation({
    mutationFn: () => freezeStrategy({ id: strategyId, freeze: !isFrozen }),
    onSuccess: (result) => {
      onSuccessGeneric(result);
      setIsFrozen(result.success ? !isFrozen : isFrozen);
    },
    onError,
  });
  const onFreeze = () => freezeMutation.mutate();

  const rebalanceMutation = useMutation({
    mutationFn: () => rebalanceStrategy(strategyId),
    onSuccess: onSuccessGeneric,
    onError,
  });
  const onRebalance = () => rebalanceMutation.mutate();

  const sendToCsMutation = useMutation({
    mutationFn: () => sendStrategyToCs(strategyId),
    onSuccess: onSuccessGeneric,
    onError,
  });
  const onSendToCs = () => sendToCsMutation.mutate();

  const getApiKeysMutation = useMutation({
    mutationFn: () => getApiKey(strategyId),
    onSuccess: (result) => {
      setApiKeys(result?.result);
      onSuccessGeneric(result);
    },
    onError,
  });
  const onGetApiKeys = () => getApiKeysMutation.mutate();

  return {
    loading:
      freezeMutation.isPending ||
      rebalanceMutation.isPending ||
      sendToCsMutation.isPending ||
      getApiKeysMutation.isPending,
    isFrozen,
    apiKeys,
    resetApiKeys,
    onRecalcHistory,
    onDownload,
    onFreeze,
    onRebalance,
    onSendToCs,
    onGetApiKeys,
  };
};
